// BuddyBooksDashboard.jsx
import React, { Component } from 'react';
import { Typography, Paper } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import moment from 'moment';
import { getDashboardChartData, getStudent } from '../../../redux/actions';
import AiSummary from '../../components/Shared/Dashboard/AiSummary';
import QuickActionsContainer from '../../components/Shared/Dashboard/QuickActionsContainer';

// Debug configuration
const DEBUG = true;
const log = (...args) => {
    if (DEBUG) {
        console.log('[BuddyBooksDashboard]', ...args);
    }
};

// Dummy data for development/testing
const getDummyQuickActions = () => {
    const isProduction = process.env.NODE_ENV === 'production';
    if (isProduction) return [];
    
    return [
        {
            "studentId": -6903367318792312000,
            "date": [2025, 6, 2, 4, 50, 3, 352000000],
            "type": "READING LEVEL DOWN",
            "name": "Reading Mode Adjustment Recommended",
            "description": "Student is struggling with paragraph mode. Would you like to switch to sentence mode?",
            "acknowledged": "No"
        },
        {
            "studentId": -6903367318792312000,
            "date": [2025, 6, 2, 4, 51, 15, 123000000],
            "type": "BOOK LEVEL UP",
            "name": "Reading Level Progress",
            "description": "Student is performing well. Ready to move up to the next reading level?",
            "acknowledged": "No"
        },
        {
            "studentId": -6903367318792312000,
            "date": [2025, 6, 2, 4, 52, 30, 456000000],
            "type": "OTHER",
            "name": "System Notice",
            "description": "A general notice was generated for this student. Please review and acknowledge.",
            "acknowledged": "No"
        },
        {
            "studentId": -6903367318792312000,
            "date": [2025, 6, 2, 4, 53, 45, 789000000],
            "type": "OTHER",
            "name": "Low Accuracy and Fluency",
            "description": "Student is showing below-average performance in these areas. View detailed metrics.",
            "acknowledged": "No"
        },
        {
            "studentId": -6903367318792312000,
            "date": [2025, 6, 2, 4, 54, 51, 609000000],
            "type": "SKIPRATE",
            "name": "High Skip Rate",
            "description": "Student is skipping 25% of passages, which is higher than the class average of 10%.",
            "acknowledged": "No"
        }
    ];
};

const styles = (theme) => ({
    root: {
        padding: theme.spacing(3),
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    header: {
        marginBottom: theme.spacing(4),
    },
    title: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(1),
        color: '#333',
    },
    subtitle: {
        color: '#666',
        fontSize: '1.1rem',
    },
    section: {
        marginBottom: theme.spacing(3),
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
});

class BuddyBooksDashboard extends Component {
    state = {
        summary: null,
        quickActions: [],
        loading: true,
        error: null
    };

    componentDidMount() {
        this.fetchDashboardData();
        this.props.getStudent(this.props.id);
    }

    fetchDashboardData = () => {
        const endDate = moment().add(1, 'day').format('YYYY-MM-DD');
        const startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
        
        getDashboardChartData(this.props.id, startDate, endDate).then(data => {
            if (data) {
                log('Dashboard data response:', data);
                log('Available keys in response:', Object.keys(data));
                
                // Extract data from the nested structure
                // The data appears to be nested under a studentId key
                const studentData = data[this.props.id] || data[Object.keys(data)[0]];
                
                if (studentData) {
                    log('Student data found:', studentData);
                    log('Student data keys:', Object.keys(studentData));
                    log('Summary object:', studentData.summary);
                    log('Quick Actions from studentData:', studentData.quickActions);
                    
                    // Get real quick actions and merge with dummy data if not in production
                    const realQuickActions = studentData.quickActions || [];
                    const dummyQuickActions = getDummyQuickActions();
                    const allQuickActions = [...realQuickActions, ...dummyQuickActions];
                    
                    log('Real quick actions:', realQuickActions);
                    log('Dummy quick actions:', dummyQuickActions);
                    log('Combined quick actions:', allQuickActions);
                    
                    this.setState({ 
                        summary: studentData.summary || null,
                        quickActions: allQuickActions,
                        loading: false
                    });
                } else {
                    // Try to get quickActions from the root level if nested structure doesn't exist
                    log('No nested student data found, trying root level data extraction');
                    log('Root level summary:', data.summary);
                    log('Root level quickActions:', data.quickActions);
                    
                    // Also check if quickActions might be under a different key
                    log('Checking for quickActions under different keys:');
                    log('data.quickActions:', data.quickActions);
                    log('data.quick_actions:', data.quick_actions);
                    log('data.actions:', data.actions);
                    
                    // Get real quick actions and merge with dummy data if not in production
                    const realQuickActions = data.quickActions || data.quick_actions || data.actions || [];
                    const dummyQuickActions = getDummyQuickActions();
                    const allQuickActions = [...realQuickActions, ...dummyQuickActions];
                    
                    log('Real quick actions (root level):', realQuickActions);
                    log('Dummy quick actions:', dummyQuickActions);
                    log('Combined quick actions:', allQuickActions);
                    
                    this.setState({ 
                        summary: data.summary || null,
                        quickActions: allQuickActions,
                        loading: false
                    });
                }
            } else {
                log('No data received from API');
                this.setState({ 
                    loading: false,
                    error: 'No data available'
                });
            }
        }).catch(error => {
            console.error('[BuddyBooksDashboard] Error fetching dashboard data:', error);
            this.setState({
                loading: false,
                error: 'Failed to load dashboard data'
            });
        });
    };

    render() {
        const { classes, student } = this.props;
        const { summary, quickActions, loading, error } = this.state;

        return (
            <div className={classes.root}>
                {/* Header Section */}
                <div className={classes.header}>
                    <Typography variant="h3" className={classes.title}>
                        Dashboard
                    </Typography>
                    <Typography variant="h6" className={classes.subtitle}>
                        Overview and quick actions for {student.firstName ? `${student.firstName}'s` : 'student'} progress
                    </Typography>
                </div>

                {/* AI Summary Section */}
                <div className={classes.section}>
                    <AiSummary 
                        summary={summary}
                        loading={loading}
                        error={error}
                        currentBuddyBooksLevel={student && student.currentBuddyBooksLevel}
                    />
                </div>

                {/* Quick Actions Section */}
                <div className={classes.section}>
                    <QuickActionsContainer 
                        quickActions={quickActions}
                        loading={loading}
                        error={error}
                    />
                </div>
            </div>
        );
    }
}

const mapState = ({ platform: { student } }) => {
    return {
        student
    }
};

const mapDispatch = (dispatch) => {
    return {
        getStudent: (id) => dispatch(getStudent(id))
    }
};

export default connect(mapState, mapDispatch)(withStyles(styles)(BuddyBooksDashboard));